<template>
  <div class="contents">
    <div class="layout">
      <div class="page-top">
        <h2>공모전 관리</h2>
        <div class="breadcrumbs">
          <span>컨텐츠 관리</span>
          <span class="color-black">공모전 관리</span>
        </div>
      </div>

      <div v-if="dataLoading" class="loading-container">
        <p>데이터를 로딩 중입니다...</p>
      </div>

      <div v-else-if="formReady">
        <div class="white-panel mt30">
          <!-- 헤더 -->
          <div class="header">
            <p>공모전 정보와 신청 폼을 설정하세요</p>
          </div>

          <!-- 공모전 기본 정보 -->
          <div class="card">
            <h2>기본 정보</h2>
            <FormKit
              v-if="
                articleNewsOptions.length >= 1 && organizationOptions.length > 1
              "
              ref="contestForm"
              type="form"
              :value="contestData"
              @submit="handleSubmit"
              submit-label="저장"
              :submit-attrs="{ class: 'btn-primary' }"
              :actions="false"
            >
              <div v-if="contestData.preview_image_url" class="image-preview">
                <label>기존 이미지 미리보기:</label>
                <img
                  :src="contestData.preview_image_url"
                  alt="기존 이미지"
                  style="
                    max-width: 300px;
                    max-height: 200px;
                    display: block;
                    margin-top: 8px;
                    border-radius: 4px;
                  "
                />
              </div>

              <FormKit
                type="file"
                name="preview_image"
                label="미리보기 이미지 업로드"
                accept="image/*"
                help="이미지 파일을 업로드하세요 (jpg, png 등)"
                @input="onImageSelected"
              />
              <FormKit
                type="text"
                name="title"
                label="공모전 제목"
                placeholder="공모전 제목을 입력하세요"
                validation="required"
              />
              <FormKit
                type="textarea"
                name="description"
                label="공모전 설명"
                placeholder="공모전에 대한 자세한 설명을 입력하세요"
                rows="4"
              />
              <FormKit
                type="select"
                name="article_news_id"
                label="관련 뉴스 선택"
                :options="articleNewsOptions"
                validation="required"
              />
              <FormKit
                type="select"
                name="organization_id"
                label="주최 기관"
                :options="organizationOptions"
                validation="required"
              />
              <FormKit
                type="datetime-local"
                name="start_date"
                label="공모전 시작일"
                validation="required"
              />
              <FormKit
                type="datetime-local"
                name="end_date"
                label="공모전 종료일"
                validation="required"
              />
              <FormKit type="checkbox" name="is_active" label="공모전 활성화" />
            </FormKit>
            <div v-else>
              <div v-if="articleNewsFetchError">
                공모 NEWS 목록을 불러오지 못했습니다 (HTTP {{ articleNewsFetchError.response?.status }}). 권한·네트워크를 확인하세요.
              </div>
              <div v-else-if="articleNewsOptions.length === 0">
                news[공모]를 먼저 등록하세요. (현재 공모전 신청폼을 연결할 수
                있는 news[공모]가 없습니다.)
              </div>
              <div v-else-if="organizationOptions.length <= 1">
                조직 정보를 로딩 중입니다...
              </div>
              <div v-else>데이터를 로딩 중입니다...</div>
            </div>
          </div>

          <!-- 신청 폼 필드 설정 -->
          <div class="card">
            <div class="card-header">
              <h2>신청 폼 필드</h2>
              <button @click="showAddFieldModal = true" class="btn-success">
                <span class="icon">+</span>
                필드 추가
              </button>
            </div>

            <!-- 필드 목록 -->
            <div class="fields-list">
              <div
                v-for="(field, index) in formFields"
                :key="field.id"
                class="field-item"
              >
                <div class="field-header">
                  <div class="field-info">
                    <h3>{{ field.label }}</h3>
                    <p class="field-type">
                      {{ getFieldTypeLabel(field.type) }}
                    </p>
                  </div>
                  <div class="field-actions">
                    <button @click="editField(index)" class="btn-edit">
                      편집
                    </button>
                    <button @click="removeField(index)" class="btn-danger">
                      삭제
                    </button>
                  </div>
                </div>
                <div class="field-preview">
                  <!--
                  <div class="debug-info">
                    <small>옵션 확인: {{ JSON.stringify(field.options) }}</small>
                  </div>
                  -->
                  <FormKit
                    :type="field.type"
                    :label="field.label"
                    :placeholder="field.placeholder"
                    :validation="field.validation"
                    :options="field.options || []"
                    :accept="field.accept"
                    :help="field.help"
                    :disabled="true"
                  />
                </div>
              </div>
            </div>

            <!-- 필드가 없을 때 -->
            <div v-if="formFields.length === 0" class="empty-state">
              <p>아직 추가된 필드가 없습니다.</p>
              <button @click="showAddFieldModal = true" class="btn-primary">
                첫 번째 필드 추가하기
              </button>
            </div>
          </div>

          <!-- 미리보기 -->
          <div class="card">
            <h2>신청 폼 미리보기</h2>
            <div class="preview-container">
              <!--        <div class="debug-info">
                        <h4>디버그 정보:</h4>
                        <pre>{{ JSON.stringify(formFields, null, 2) }}</pre>
                      </div>-->
              <FormKit
                type="form"
                :value="{}"
                @submit="handlePreviewSubmit"
                submit-label="신청하기 테스트"
                :submit-attrs="{ class: 'btn-primary' }"
              >
                <FormKit
                  v-for="field in formFields"
                  :key="field.id"
                  :type="field.type"
                  :name="field.name"
                  :label="field.label"
                  :help="field.help"
                  :placeholder="field.placeholder"
                  :validation="field.validation"
                  :options="field.options || []"
                  :accept="field.accept"
                  :multiple="field.multiple"
                  :disabled="field.disabled"
                />
              </FormKit>
            </div>
          </div>
        </div>

        <div class="button-panel mt30">
          <div class="left">
            <FormKit
              type="button"
              @click="goToList"
              style="
                background-color: white;
                color: black;
                border: 1px solid #00000036;
              "
            >
              목록
            </FormKit>
          </div>
          <div class="right">
            <!-- 별도 제출 버튼 -->
            <FormKit type="button" class="btn-primary" @click="submitForm">
              {{ uuid ? '수정하기' : '등록하기' }}
            </FormKit>
          </div>
        </div>

        <!-- 필드 추가/편집 모달 -->
        <div
          v-if="showAddFieldModal || editingField !== null"
          class="modal-overlay"
        >
          <div class="modal">
            <div class="modal-header">
              <h3>{{ editingField !== null ? '필드 편집' : '필드 추가' }}</h3>
              <button @click="closeModal" class="btn-close">×</button>
            </div>
            <div class="modal-body">
              <FormKit
                :key="formKey"
                type="form"
                :value="currentField"
                @submit="saveField"
                submit-label="저장"
                :submit-attrs="{ class: 'btn-primary' }"
              >
                <FormKit
                  type="select"
                  name="fieldKey"
                  label="필드 선택"
                  placeholder="필드 선택를 선택하세요"
                  :options="fieldTypes"
                  validation="required"
                  @input="onFieldTypeChange"
                />
                <FormKit type="text" name="type" label="필드 타입" disabled />
                <FormKit
                  type="text"
                  name="name"
                  label="필드 이름 (영문)"
                  placeholder="field_name"
                  help="입력하지 않으면 자동으로 생성됩니다. (예: text_1)"
                  disabled
                />
                <FormKit
                  type="text"
                  name="label"
                  label="필드 라벨"
                  placeholder="사용자에게 보여질 라벨"
                  help="사용자에게 보여질 라벨"
                  validation="required"
                />
                <FormKit
                  type="text"
                  name="placeholder"
                  label="플레이스홀더"
                  placeholder="입력 힌트 텍스트"
                  help="입력칸이 빈칸인 경우 보여지는 텍스트"
                />
                <FormKit
                  type="textarea"
                  name="help"
                  label="도움말"
                  placeholder="필드에 대한 추가 설명"
                  help="필드에 대한 추가 설명"
                />
                <FormKit
                  type="checkbox"
                  name="required"
                  label="필수 입력"
                  :value="false"
                  help="체크하면 필수 입력으로 지정"
                />

                <!-- 선택형 필드 옵션 -->
                <div
                  v-if="
                    ['select', 'radio', 'checkbox'].includes(currentField.type)
                  "
                  class="options-section"
                >
                  <h4>선택 옵션</h4>
                  <div class="options-list">
                    <div
                      v-for="(option, index) in currentField.options"
                      :key="index"
                      class="option-item"
                    >
                      <input
                        v-model="option.label"
                        type="text"
                        placeholder="옵션 라벨"
                        class="option-input"
                      />
                      <!--
                      <input
                          v-model="option.value"
                          type="text"
                          placeholder="옵션 값"
                          class="option-input"
                          readonly
                      />
                      -->
                      <button
                        @click="removeOption(index)"
                        class="btn-remove"
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <button
                    @click="addOption"
                    type="button"
                    class="btn-secondary"
                  >
                    옵션 추가
                  </button>
                  <button
                    @click="addEtcOption"
                    type="button"
                    class="btn-secondary"
                  >
                    + 기타 항목
                  </button>
                </div>

                <!-- 파일 업로드 설정 -->
                <div v-if="currentField.type === 'file'" class="file-section">
                  <h4>파일 업로드 설정</h4>
                  <FormKit
                    type="text"
                    name="accept"
                    label="허용할 파일 형식"
                    placeholder="입력하지 않으면 전체파일이 허용됩니다."
                    help="쉼표로 구분된 MIME 타입 또는 확장자를 스페이스 없이 입력하세요. 예: image/*,video/*,audio/*,.pdf,.docx"
                  />
                  <FormKit
                    type="number"
                    name="maxFileSize"
                    label="개별 파일 최대 크기 (MB)"
                    placeholder="개별 파일 최대 값을 작성해주새요.(자연수만 입력)"
                    min="1"
                    max="2000"
                    help="1~2000 사용"
                    validation="required"
                  />
                  <FormKit
                    type="checkbox"
                    name="multiple"
                    label="다중 파일 업로드 허용(최대 10개)"
                    help="체크 시 사용자가 여러 파일을 업로드할 수 있습니다."
                  />
                </div>

                <!-- 회원 정보 필드 설정 -->
                <div
                  v-if="isMemberInfoField(currentField.fieldKey)"
                  class="member-info-section"
                >
                  <h4>회원 정보 필드 설정</h4>
                  <FormKit
                    type="checkbox"
                    name="disabled"
                    label="사용자 수정 금지"
                    help="체크하면 사용자가 미리 입력된 값을 수정할 수 없습니다."
                  />
                </div>
              </FormKit>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { apiClient } from '@/services/auth-header'
import { useRoute, useRouter } from 'vue-router'
import { utcToLocalDateTimeInput, localDateTimeInputToUtc } from '@/utils/timezone'

const route = useRoute()
const router = useRouter()
const formReady = ref(false)
const dataLoading = ref(true)

const uuid = ref(route.params.uuid)

const API_URL = '/contents/contests'
const contestData = reactive({
  title: '',
  description: '',
  preview_image: '',
  article_news_id: null,
  is_active: true,
  start_date: '',
  end_date: '',
  organization_id: null,
})

const formFields = ref([])
const showAddFieldModal = ref(false)
const editingField = ref(null)

const defaultField = {
  fieldKey: '',
  type: '',
  name: '',
  label: '',
  placeholder: '',
  help: '',
  required: false,
  options: [],
  accept: '',
  maxFileSize: null,
  multiple: false,
  disabled: false,
}

const currentField = ref({ ...defaultField })

const resetCurrentField = () => {
  Object.assign(currentField.value, { ...defaultField, options: [] })
}

const fieldTypes = [
  {
    group: '회원 정보',
    options: [
      { label: '아티스트명', value: 'mimio_artist_name' },
      { label: '실명', value: 'mimio_real_name' },
      { label: '이메일', value: 'mimio_email' },
      { label: '전화번호', value: 'mimio_phone' },
      { label: '주소', value: 'mimio_address' },
      { label: '생년월일', value: 'mimio_birth_date' },
      { label: '포트폴리오', value: 'mimio_portfolio' },
    ],
  },
  {
    group: '추가 질문',
    options: [
      { label: '텍스트', value: 'text' },
      { label: '긴 텍스트', value: 'textarea' },
      { label: '단일선택', value: 'radio' },
      { label: '중복선택', value: 'checkbox' },
      { label: '파일 업로드', value: 'file' },
    ],
  },
]

// 회원 정보 필드 기본값 매핑
const memberFieldDefaults = {
  mimio_artist_name: {
    type: 'text',
    label: '아티스트명',
    placeholder: '프로필 관리 페이지에서 입력한 이름',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_real_name: {
    type: 'text',
    label: '실명',
    placeholder: '회원 가입 시 등록한 실명',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_email: {
    type: 'text',
    label: '이메일',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_phone: {
    type: 'text',
    label: '전화번호',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_address: {
    type: 'text',
    label: '주소',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_birth_date: {
    type: 'text',
    label: '생년월일',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
  mimio_portfolio: {
    type: 'text',
    label: '포트폴리오',
    placeholder: '포트폴리오 제작 바로가기 버튼을 눌러주세요.',
    help: '마이 페이지 > 나의 정보에서 자동으로 가져옵니다.',
  },
}

const formKey = ref(0)
// 필드 타입 변경 시 자동 설정
const onFieldTypeChange = value => {
  resetCurrentField()
  currentField.value.fieldKey = value
  currentField.value.type = value

  if (memberFieldDefaults[value]) {
    const defaults = memberFieldDefaults[value]
    currentField.value = {
      fieldKey: value,
      type: defaults.type,
      name: value,
      label: defaults.label,
      placeholder: defaults.placeholder,
      help: defaults.help,
      required: true,
      options: [],
      disabled: true,
    }
  } else if (['select', 'radio', 'checkbox'].includes(value)) {
    currentField.value.options = [{ label: '옵션1', value: 'option1' }]
  }
  formKey.value++
}

const onImageSelected = files => {
  if (
    Array.isArray(files) &&
    files.length > 0 &&
    files[0].file instanceof File
  ) {
    const file = files[0].file
    const reader = new FileReader()

    reader.onload = e => {
      contestData.preview_image_url = e.target.result // base64 URL로 미리보기 설정
    }

    reader.readAsDataURL(file)
  } else {
    // 파일이 없으면 미리보기 초기화
    contestData.preview_image_url = ''
  }
}

const getFieldTypeLabel = type => {
  for (const group of fieldTypes) {
    const match = group.options.find(option => option.value === type)
    if (match) {
      return match.label
    }
  }
  return type
}

const addOption = () => {
  const optionNum = currentField.value.options.length + 1
  currentField.value.options.push({
    label: `옵션${optionNum}`,
    value: `option${optionNum}`,
  })
}

const addEtcOption = () => {
  const exists = currentField.value.options.some(opt => opt.value === 'ETC')
  if (!exists) {
    currentField.value.options.push({
      label: '기타',
      value: 'ETC',
    })
  } else {
    alert('이미 "기타" 항목이 추가되어 있습니다.')
  }
}

const removeOption = index => {
  currentField.value.options.splice(index, 1)
}

const isMemberInfoField = fieldType => {
  return fieldType && fieldType.startsWith('mimio_')
}

const makeUniqueNameFromType = (type, existingNames) => {
  let index = 1
  let candidate = `${type}_${index}`
  while (existingNames.includes(candidate)) {
    index++
    candidate = `${type}_${index}`
  }
  return candidate
}

const saveField = data => {
  const existingNames = formFields.value.map(f => f.name)

  // name이 없으면 자동 생성
  if (!data.name || data.name.trim() === '') {
    data.name = makeUniqueNameFromType(data.type, existingNames)
  }

  const fieldData = {
    ...data,
    id:
      editingField.value !== null
        ? formFields.value[editingField.value].id
        : Date.now(),
    validation: data.required ? 'required' : '',
  }

  if (['select', 'radio', 'checkbox'].includes(fieldData.type)) {
    fieldData.options = currentField.value.options.filter(
      opt => opt.label && opt.value
    )
  }

  if (editingField.value !== null) {
    formFields.value[editingField.value] = fieldData
  } else {
    formFields.value.push(fieldData)
  }

  closeModal()
}

const editField = index => {
  editingField.value = index
  const field = formFields.value[index]

  Object.assign(currentField.value, {
    ...field,
    required: field.validation === 'required',
    options: field.options || [],
  })

  showAddFieldModal.value = true
}

const removeField = index => {
  if (confirm('정말 이 필드를 삭제하시겠습니까?')) {
    formFields.value.splice(index, 1)
  }
}

const closeModal = () => {
  showAddFieldModal.value = false
  editingField.value = null
  resetCurrentField()
}

const contestForm = ref(null)

function submitForm() {
  // FormKit 인스턴스에서 submit() 호출
  contestForm.value.node.submit()
}

const handleSubmit = async data => {
  const formSchema = formFields.value.map((field, index) => ({
    fieldKey: field.fieldKey,
    type: field.type,
    name: field.name,
    label: field.label,
    placeholder: field.placeholder,
    validation: field.validation, //required
    options: field.options || [],
    accept: field.accept,
    maxFileSize: field.maxFileSize,
    help: field.help,
    multiple: field.multiple,
    disabled: field.disabled,
    sort: index, // 현재 배열 순서 기반
  }))

  const reqDto = {
    articleNewsId: data.article_news_id,
    title: data.title,
    description: data.description,
    formSchemaJson: JSON.stringify(formSchema),
    isActive: data.is_active,
    organizationId: data.organization_id,
    startDate: localDateTimeInputToUtc(data.start_date),
    endDate: localDateTimeInputToUtc(data.end_date),
  }

  const formData = new FormData()
  formData.append(
    'reqDto',
    new Blob([JSON.stringify(reqDto)], { type: 'application/json' })
  )

  const previewFiles = data.preview_image
  if (
    Array.isArray(previewFiles) &&
    previewFiles.length > 0 &&
    previewFiles[0].file instanceof File
  ) {
    formData.append('previewImage', previewFiles[0].file)
  }
  try {
    if (uuid.value) {
      await apiClient.put(`${API_URL}/${uuid.value}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      alert('수정되었습니다.')
      window.location.reload()
    } else {
      await apiClient.post(API_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      alert('등록되었습니다.')
      await router.push({ name: 'ContestManagement' })
    }
  } catch (e) {
    console.error('저장 실패:', e)
    alert('저장 중 오류가 발생했습니다.')
  }
}

const goToList = () => {
  router.push({ name: 'ContestManagement' })
}

const handlePreviewSubmit = data => {
  console.log('미리보기 제출 데이터:', data)
}

const fetchContest = async () => {
  try {
    const { data } = await apiClient.get(`${API_URL}/${uuid.value}`)

    // 기본 정보 바인딩
    contestData.title = data.title
    contestData.description = data.description
    contestData.article_news_id = data.articleNewsId
    contestData.is_active = data.isActive
    contestData.preview_image_url = data.previewImageUrl
    contestData.organization_id = data.organizationId

    // 날짜 데이터 처리 (UTC를 브라우저 로컬 타임존으로 변환)
    contestData.start_date = utcToLocalDateTimeInput(data.startDate)
    contestData.end_date = utcToLocalDateTimeInput(data.endDate)

    // 폼 필드 바인딩
    if (data.formSchemaJson) {
      formFields.value = JSON.parse(data.formSchemaJson)
    } else {
      formFields.value = []
    }
  } catch (e) {
    console.error('공모전 불러오기 실패:', e)
    alert('공모전 정보를 불러오는 데 실패했습니다.')
  }
}

const articleNewsOptions = ref([])
const articleNewsFetchError = ref(null)
const organizationOptions = ref([{ label: '로딩중...', value: null }])

const fetchArticleNewsOptions = async () => {
  try {
    const response = await apiClient.get(`${API_URL}/competition-news`, {
      params: { contestUuid: uuid.value || null },
    })

    articleNewsOptions.value = response.data.map(item => ({
      label: item.title, // 뉴스 제목
      value: item.id, // 뉴스 ID
    }))

    if (articleNewsOptions.value.length > 0) {
      contestData.article_news_id = articleNewsOptions.value[0].value
    }
  } catch (e) {
    articleNewsFetchError.value = e
    console.error('뉴스 목록 불러오기 실패', e)
  }
}

const fetchOrganizationOptions = async () => {
  try {
    const response = await apiClient.get('/organizations')

    organizationOptions.value = [
      { label: '주최 기관을 선택하세요', value: null },
      ...response.data.map(item => ({
        label: item.name, // 조직 이름
        value: item.id, // 조직 ID
      })),
    ]
  } catch (e) {
    console.error('조직 목록 불러오기 실패', e)
    organizationOptions.value = [{ label: '조직 목록 로드 실패', value: null }]
  }
}

onMounted(async () => {
  try {
    await Promise.all([fetchArticleNewsOptions(), fetchOrganizationOptions()])
    if (uuid.value) {
      await fetchContest()
    }
  } catch (error) {
    console.error('데이터 로딩 실패:', error)
  } finally {
    dataLoading.value = false
    formReady.value = true
  }
})
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 1.1rem;
  color: #666;
}

.header {
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.header p {
  color: #666;
  font-size: 1rem;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
  padding: 24px;
  margin-bottom: 24px;
}

.card h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.image-preview {
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.fields-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-item {
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.field-info h3 {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.field-type {
  font-size: 0.875rem;
  color: #666;
  margin: 0;
}

.field-actions {
  display: flex;
  gap: 8px;
}

.field-preview {
  background: white;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e1e5e9;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-state p {
  margin-bottom: 16px;
}

.preview-container {
  border: 2px dashed #e1e5e9;
  border-radius: 8px;
  padding: 24px;
  background: #f8f9fa;
}

/* 버튼 스타일 */
.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-success {
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.2s;
}

.btn-success:hover {
  background: #1e7e34;
}

.btn-edit {
  background: #ffc107;
  color: #333;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.2s;
}

.btn-edit:hover {
  background: #e0a800;
}

.btn-danger {
  background: #dc3545;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.2s;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-remove {
  background: #dc3545;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  font-size: 1.1rem;
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  padding: 20px;
}

.options-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e1e5e9;
}

.options-section h4 {
  margin-bottom: 16px;
  font-size: 1rem;
  font-weight: 500;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  font-size: 0.875rem;
}

.option-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* 파일 업로드 설정 스타일 */
.file-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e1e5e9;
}

.file-section h4 {
  margin-bottom: 16px;
  font-size: 1rem;
  font-weight: 500;
}

.extensions-section {
  margin-top: 16px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.extensions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.extension-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.extension-item:hover {
  background: #f8f9fa;
}

.extension-item input[type='checkbox'] {
  margin: 0;
}

.custom-extension {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.extension-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  font-size: 0.875rem;
}

.extension-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.selected-extensions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  font-size: 0.875rem;
}

.extension-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
}

.tag-remove {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.tag-remove:hover {
  background: rgba(25, 118, 210, 0.1);
}

.debug-info {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 16px;
  font-size: 0.75rem;
  color: #6c757d;
}

.debug-info h4 {
  margin: 0 0 8px 0;
  font-size: 0.875rem;
}

.debug-info pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
